// Maximum serial-number length accepted by the backend
// (StringConstraints(max_length=40) in management-api/src/schema/asset/common.py).
// Exported so UI controls (PadControls's length cap, length-related copy)
// can stay in sync without duplicating the magic number.
export const MAX_SERIAL_LENGTH = 40;

export interface PadConfig {
  enabled: boolean;
  length: number;
}

// Apostrophe-like characters that the backend's _strip_apostrophes also
// removes: ASCII single quote plus Unicode left/right single quotation marks
// (which LibreOffice "smart quotes" autocorrect may substitute). Keeping
// the frontend list in sync with management-api/src/schema/asset/common.py
// means the preview shows exactly what the backend will store.
const APOSTROPHE_CHARS = ["'", "‘", "’"];

// ASCII " plus Unicode smart double-quotes — backend rejects these via
// _reject_quotes. Mirror here so the preview catches them rather than
// surprising the user with a 422.
const QUOTE_CHARS = ['"', "“", "”"];

// OWASP CSV-injection vectors. Asset register exports round-trip through
// Excel/LibreOffice, which evaluate any cell starting with one of these
// as a formula. The backend rejects these in SerialNumberStr; mirror the
// rule on the frontend so the row is marked red in the preview and
// excluded from submit rather than silently 422-ing on Upload.
//
// Also imported by `components/Forms/yupValidators.ts` (yupSerialNumber)
// so the manual create-asset form and the CSV-upload preview share one
// source of truth.
export const CSV_FORMULA_LEADING_CHARS = ["=", "+", "-", "@"];

function stripApostrophes(value: string): string {
  return APOSTROPHE_CHARS.reduce((acc, ch) => acc.split(ch).join(""), value);
}

function hasApostrophe(value: string): boolean {
  return APOSTROPHE_CHARS.some((ch) => value.includes(ch));
}

function hasOuterWhitespace(value: string): boolean {
  return value.trim() !== value;
}

function hasInternalWhitespace(value: string): boolean {
  // After trim(), any remaining whitespace must be internal — those are
  // what the backend's _reject_whitespace rejects.
  return /\s/.test(value);
}

function hasQuoteChar(value: string): boolean {
  return QUOTE_CHARS.some((ch) => value.includes(ch));
}

// Issue #1050: spreadsheets that format cells as numbers strip leading zeros,
// turning serials like "01234567" into "1234567". This pads numeric-only
// serials with leading zeros to restore the original width before upload.
// The value is always returned with apostrophes stripped and leading/
// trailing whitespace trimmed (mirroring the backend's `_strip_apostrophes`
// + `strip_whitespace=True`) so the preview reflects what the backend will
// actually store; padding then applies on top when the cleaned value is
// all-digit and shorter than the target.
export function padSerialNumber(
  serial: string | null | undefined,
  config: PadConfig,
): string | null | undefined {
  if (serial == null) return serial;
  const cleaned = stripApostrophes(serial).trim();
  if (!config.enabled || config.length <= 0) return cleaned;
  if (!/^\d+$/.test(cleaned)) return cleaned;
  if (cleaned.length >= config.length) return cleaned;
  return cleaned.padStart(config.length, "0");
}

interface DefinitionError {
  field?: string;
  error?: string;
}

// Re-exported from GenericUploadPage so the helper stays decoupled from the
// kubb-generated types, but the field shape matches what UploadDefinition
// declares (so the field is round-trippable through that shared type).
export type { SimpleParseWarning as DefinitionWarning } from "./GenericUploadPage";
import type { SimpleParseWarning } from "./GenericUploadPage";

// Build a human-readable description of what the helper sanitised away
// from the original serial value (for surfacing on the preview page).
function cleanupWarning(rawSerial: string): string | null {
  const a = hasApostrophe(rawSerial);
  const w = hasOuterWhitespace(rawSerial);
  if (a && w) return "Apostrophes and surrounding whitespace were removed";
  if (a) return "Apostrophes were removed";
  if (w) return "Surrounding whitespace was removed";
  return null;
}

function rejectIfPresent(
  cleanedSerial: string,
): { field: string; error: string } | null {
  // Each rejection mirrors a backend AfterValidator on SerialNumberStr.
  // Keep these checks aligned with `management-api/src/schema/asset/common.py`.
  if (
    cleanedSerial.length > 0 &&
    CSV_FORMULA_LEADING_CHARS.includes(cleanedSerial[0])
  ) {
    return {
      field: "serial_number",
      error: "serial_number must not start with =, +, -, or @",
    };
  }
  if (hasInternalWhitespace(cleanedSerial)) {
    return {
      field: "serial_number",
      error: "serial_number must not contain internal whitespace",
    };
  }
  if (hasQuoteChar(cleanedSerial)) {
    return {
      field: "serial_number",
      error: "serial_number must not contain quote characters",
    };
  }
  return null;
}

// Generic shape — keeps this helper free of the kubb-generated types so it
// stays unit-testable without pulling the full frontend module graph.
// We update BOTH `object.serial_number` (the value sent to the API) AND
// `original.serial_number` (the value rendered by UploadPreviewTable) so
// the preview is a live, faithful view of what will be uploaded.
//
// If stripping leaves the serial empty (the CSV cell was just an apostrophe
// or whitespace that the backend would also reject with a 422), or the
// cleaned value would trip one of the backend's other validators (leading
// CSV-formula char, internal whitespace, quote character), we flip the row
// into an error state so the user sees the problem in the preview rather
// than discovering it after Upload.
//
// When stripping changed the value (but the row is still valid), we attach
// a warning so the preview page can show a banner — the value is silently
// mutated to match what the backend would store, and the user deserves a
// heads-up.
//
// NB: callers should pass the raw parse result, NOT the output of a prior
// `applyPadToDefinition` call. Chaining would double-apply cleanup and
// duplicate warnings.
export function applyPadToDefinition<
  T extends { serial_number?: string | null },
  D extends {
    success: boolean;
    object?: T;
    original?: { serial_number?: string | null } & Record<string, unknown>;
    errors?: DefinitionError[];
    warnings?: SimpleParseWarning[];
  },
>(definition: D, config: PadConfig): D {
  if (!definition.success || !definition.object) return definition;
  const before = definition.object.serial_number;
  const after = padSerialNumber(before, config);
  const cleanedSerial = after ?? null;

  // Run the rejection checks against the CLEANED value and BEFORE the
  // same-ref short-circuit, because a raw "=cmd" / "12 34" / `12"34`
  // (no apostrophes/whitespace to clean) would otherwise flow through
  // unchanged. The backend would 422 these — flag in the preview so
  // they show red and are excluded from submit.
  if (typeof cleanedSerial === "string") {
    const rejection = rejectIfPresent(cleanedSerial);
    if (rejection) {
      return {
        ...definition,
        success: false,
        object: { ...definition.object, serial_number: cleanedSerial },
        original: definition.original
          ? { ...definition.original, serial_number: cleanedSerial }
          : definition.original,
        errors: [...(definition.errors ?? []), rejection],
      };
    }
  }

  if (after === before) return definition;

  // Empty after stripping → the backend's min_length=1 would reject this
  // with a 422. Surface it as a preview-time row error instead so the
  // user can fix it before submitting.
  if (cleanedSerial === "") {
    return {
      ...definition,
      success: false,
      object: { ...definition.object, serial_number: "" },
      original: definition.original
        ? { ...definition.original, serial_number: "" }
        : definition.original,
      errors: [
        ...(definition.errors ?? []),
        {
          field: "serial_number",
          error: "serial_number is empty after stripping apostrophes",
        },
      ],
    };
  }

  // Surface a non-blocking warning whenever the helper silently mutated
  // the value to match the backend's stripping rules. Padding alone (no
  // strip) does not produce a warning — the user opted into that via the
  // pad checkbox.
  const warningMessage =
    typeof before === "string" ? cleanupWarning(before) : null;
  const warnings = warningMessage
    ? [
        ...(definition.warnings ?? []),
        { field: "serial_number", warning: warningMessage },
      ]
    : definition.warnings;

  return {
    ...definition,
    object: { ...definition.object, serial_number: cleanedSerial },
    original: definition.original
      ? { ...definition.original, serial_number: cleanedSerial }
      : definition.original,
    ...(warnings ? { warnings } : {}),
  };
}
